/**
 * useRealTimeMode — sidebar listener for incoming OBSERVED_CONTEXT
 * messages from the Chrome extension's service worker (via the
 * sidepanel iframe bridge).
 *
 * Auth model:
 *   The web app is the only thing that holds the user's WorkOS session.
 *   The RTM resolver authenticates against the user's org's connected
 *   Enter integration (looked up server-side in Vault) — there's no
 *   shared service-account creds — so EVERY resolve call has to happen
 *   from here. The service worker is a pure pipe: it persists +
 *   broadcasts the latest OBSERVED_CONTEXT, and the iframe (this hook)
 *   does the gateway round-trip.
 *
 * Stale-request protection:
 *   ModMed lets users navigate patient-to-patient quickly. The id-ref
 *   counter (`currentRequestIdRef`) ensures a slower resolve from an
 *   older context can't overwrite the freshest result. AbortController
 *   isn't available cleanly through ts-rest, so the id guard is the
 *   primary mechanism.
 *
 * Architectural rule (per workspace conventions): hook destructures
 * store actions at the top level; the message handler closes over them.
 * Never call `useStore.getState()` inside the async handler.
 */

import { createLogger } from '@ctrl-enter/tools/utils';
import type { MatchState } from '@ctrl-enter/types';
import { ObservedContextSchema } from '@ctrl-enter/types';
import { useEffect, useRef } from 'react';
import { tsrClient } from '../api/client';
import { useRealTimeModeSetLoading, useRealTimeModeSetMatch, useRealTimeModeSetSuggestedMcpAccountId } from '../stores/real-time-mode-store';
import { useRtmSearchAllAccounts } from '../stores/system-settings-store';

const logger = createLogger('client:hook:use-real-time-mode');

type ObservedContextMessage = {
  type: 'REAL_TIME_MODE_OBSERVED_CONTEXT';
  context: unknown;
};

function isObservedContextMessage(value: unknown): value is ObservedContextMessage {
  if (typeof value !== 'object' || value === null) return false;
  return (value as { type?: unknown }).type === 'REAL_TIME_MODE_OBSERVED_CONTEXT';
}

export function useRealTimeMode(): void {
  const setLoading = useRealTimeModeSetLoading();
  const setMatch = useRealTimeModeSetMatch();
  const setSuggestedMcpAccountId = useRealTimeModeSetSuggestedMcpAccountId();
  const rtmSearchAllAccounts = useRtmSearchAllAccounts();
  const resolveMutation = tsrClient.realTimeMode.resolve.useMutation({ retry: false });
  const mutateAsyncRef = useRef(resolveMutation.mutateAsync);
  mutateAsyncRef.current = resolveMutation.mutateAsync;

  // Monotonically increasing — bumped on every incoming OBSERVED_CONTEXT.
  // A resolve only commits its result if its captured id still matches
  // this ref. Older in-flight resolves are silently dropped.
  const currentRequestIdRef = useRef(0);

  useEffect(() => {
    logger.info('🩺 [RTM] hook mounted, listener attached');

    // Pull-based handoff (chrome.storage is the source of truth):
    //   1. Bridge writes OBSERVED_CONTEXT to chrome.storage.local on
    //      click (via SW).
    //   2. Sidepanel opens.
    //   3. This hook mounts, asks the sidepanel "got anything for me?"
    //   4. Sidepanel reads chrome.storage, replies with the context if
    //      it's fresh (< 10 min old), drops it otherwise.
    //
    // Avoids the iframe-`load` vs React-mount race that any push-based
    // handoff hits. The TTL means re-opening the panel hours later
    // doesn't replay yesterday's patient — no manual cleanup needed.
    //
    // Must target `window.parent`: we're inside the sidepanel's iframe,
    // and `window.postMessage(...)` would fire on our own window, not
    // the sidepanel page that knows how to read chrome.storage.
    if (window.parent !== window) {
      window.parent.postMessage({ type: 'REAL_TIME_MODE_REQUEST_CONTEXT' }, '*');
    }

    const handler = async (event: MessageEvent) => {
      if (!isObservedContextMessage(event.data)) return;

      logger.info('🩺 [RTM] OBSERVED_CONTEXT received in hook, processing…');

      const parsed = ObservedContextSchema.safeParse(event.data.context);
      if (!parsed.success) {
        logger.warn('🩺 [RTM] dropped malformed OBSERVED_CONTEXT', parsed.error);
        return;
      }
      const context = parsed.data;
      const myId = ++currentRequestIdRef.current;

      setLoading(context);

      try {
        // When "Search all accounts" is OFF, restrict to whatever account
        // the user has selected in the chat toolbar. This avoids accidentally
        // querying (and switching to) an unintended practice in demo setups.
        const selectedAccountId =
          !rtmSearchAllAccounts
            ? (localStorage.getItem('selectedMcpAccountId') ?? undefined)
            : undefined;

        const body: { context: typeof context; restrictToAccountId?: string } = { context };
        if (selectedAccountId) body.restrictToAccountId = selectedAccountId;

        const result = await mutateAsyncRef.current({ body });
        if (myId !== currentRequestIdRef.current) return; // a newer context arrived

        logger.info('🩺 [RTM] gateway resolve returned', { status: result.status });

        if (result.status === 200) {
          const match = result.body.match as MatchState;
          setMatch(match);
          // When the gateway found the patient in a specific Enter account,
          // store the account ID so ChatLog can auto-select the right MCP.
          if (result.body.matchedAccountId) {
            setSuggestedMcpAccountId(result.body.matchedAccountId);
          }
          // If the match carries a patient balance, bubble it up to the sidepanel
          // so it can relay to the content script — which mutates the
          // "Check Balance" trigger button to show the real amount.
          if (match.status === 'matched' || match.status === 'likely') {
            const balance = (match as { data?: { financialSummary?: { outstandingPatientBalance?: number | null } } })
              .data?.financialSummary?.outstandingPatientBalance;
            if (balance !== null && balance !== undefined) {
              window.parent.postMessage({ type: 'RTM_BALANCE_UPDATE', balance }, '*');
            }
          }
        } else {
          setMatch({
            status: 'error',
            context,
            error: `Resolve failed (${result.status})`,
          });
        }
      } catch (err) {
        if (myId !== currentRequestIdRef.current) return;
        logger.error('🩺 [RTM] raw thrown value:', err);
        // ts-rest throws the full response object on 4xx/5xx, not an Error.
        // Try to pull a readable message out of whatever shape lands here.
        let message = 'Unknown error';
        if (err instanceof Error) {
          message = err.message;
        } else if (typeof err === 'object' && err !== null) {
          const e = err as Record<string, unknown>;
          if (typeof e['message'] === 'string') message = e['message'];
          else if (typeof e['status'] === 'number') message = `HTTP ${e['status']}`;
          else message = JSON.stringify(err);
        }
        logger.error('🩺 [RTM] auto-resolve failed', message);
        setMatch({ status: 'error', context, error: message });
      }
    };

    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [setLoading, setMatch, setSuggestedMcpAccountId, rtmSearchAllAccounts]);
}
